import { FC, useMemo } from 'react'

import styled from 'styled-components'

import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import Waiting from 'src/Assets/Svg/common/waiting.svg?react'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { MetaAdsCampaignStatus } from 'src/Redux/metaAdsDetailsApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { CampaignPayment } from 'src/Redux/artistOrderApi/campaignPayment'
import { CampaignPaymentInfo } from 'src/Components/CampaignPaymentInfo'

const MainInfoContainer = styled.div<{
  $approved?: boolean
}>`
  display: flex;
  height: 400px;
  padding: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ $approved }) => ($approved ? '24px' : '16px')};
  border-radius: 16px;
  border: 1px solid
    ${({ theme, $approved }) =>
      $approved ? theme.color.brandGreen : theme.color.lightGrey};
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  width: 100%;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    border: 1px solid
      ${({ theme, $approved }) =>
        $approved ? theme.color.brandGreen : 'transparent'};

    box-shadow: none;
  }
`

const InfoText = styled.span<{
  $wideText?: boolean
}>`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  max-width: ${({ $wideText }) => ($wideText ? 'none' : '400px')};
`

const TitleText = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize18};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const StyledCTAButton = styled(CTAButton)`
  padding: 0 40px;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export type InfoBlockProps = {
  campaignStatus?: MetaAdsCampaignStatus
  campaignId?: number
  payment?: CampaignPayment | null
  onStartCampaignClick?: () => void
}

export const InfoBlock: FC<InfoBlockProps> = ({
  campaignStatus,
  campaignId,
  payment,
  onStartCampaignClick,
}) => {
  const approvedStatus = campaignStatus === MetaAdsCampaignStatus.APPROVED

  const content = useMemo(() => {
    if (approvedStatus) {
      return (
        <>
          <TextWrapper>
            <TitleText>Approved to start</TitleText>
            <InfoText $wideText={true}>
              Let’s promote your music — new listeners are waiting.
            </InfoText>
          </TextWrapper>
          <StyledCTAButton onClick={onStartCampaignClick}>
            Start Campaign
          </StyledCTAButton>
        </>
      )
    }

    if (campaignStatus === MetaAdsCampaignStatus.SCHEDULED) {
      return (
        <>
          <Waiting />
          <InfoText>
            Your campaign will start within the next 24 - 72 hours
          </InfoText>
        </>
      )
    }

    return null
  }, [campaignStatus, approvedStatus, onStartCampaignClick])

  if (payment && campaignId) {
    return (
      <MainInfoContainer>
        <CampaignPaymentInfo
          payment={payment}
          platform={CampaignPlatformTypeEnum.META}
          campaignId={campaignId}
        />
      </MainInfoContainer>
    )
  }

  if (!content) {
    return null
  }

  return (
    <MainInfoContainer $approved={approvedStatus}>{content}</MainInfoContainer>
  )
}
